import { useEffect, useMemo, useState } from 'react'
import { useLocation } from 'react-router-dom'
import { BlockView } from '../components/changelog/BlockView'
import {
  entryAnchor,
  formatDateRu,
  formatDateRuShort,
  parseChangelog,
  type ChangelogEntry,
} from '../lib/changelog'

const entries = parseChangelog(__CHANGELOG__)

/**
 * Per-domain accent for the `Версии:` badges. Each of the four product
 * domains gets a distinct hue so the eye can tell at a glance which part
 * of the stack shipped. Classes are spelled out (not interpolated) so
 * Tailwind's content scanner keeps them.
 */
const DOMAIN_ACCENT: Record<string, { dot: string; label: string; bg: string }> = {
  'Бэкенд': {
    dot: 'bg-emerald-500',
    label: 'text-emerald-700 dark:text-emerald-300',
    bg: 'bg-emerald-500/10 dark:bg-emerald-400/10',
  },
  'Релей': {
    dot: 'bg-sky-500',
    label: 'text-sky-700 dark:text-sky-300',
    bg: 'bg-sky-500/10 dark:bg-sky-400/10',
  },
  'Веб': {
    dot: 'bg-amber-500',
    label: 'text-amber-700 dark:text-amber-300',
    bg: 'bg-amber-500/10 dark:bg-amber-400/10',
  },
  'Агент': {
    dot: 'bg-violet-500',
    label: 'text-violet-700 dark:text-violet-300',
    bg: 'bg-violet-500/10 dark:bg-violet-400/10',
  },
}

const DOMAIN_ACCENT_FALLBACK = {
  dot: 'bg-gray-400',
  label: 'text-gray-600 dark:text-gray-300',
  bg: 'bg-gray-100 dark:bg-gray-800',
}

export function ChangelogPage() {
  const location = useLocation()
  const [activeAnchor, setActiveAnchor] = useState<string | null>(null)

  const items = useMemo(
    () =>
      entries.map(entry => ({
        entry,
        anchor: entryAnchor(entry),
        dateLong: formatDateRu(entry.date),
        dateShort: formatDateRuShort(entry.date),
      })),
    [],
  )

  // Scroll to entry by hash on mount. Browser does this natively for already-
  // present anchors but only after layout settles — explicit scrollIntoView
  // is more reliable than relying on the timing.
  useEffect(() => {
    const hash = location.hash.slice(1)
    if (!hash) return
    const el = document.getElementById(hash)
    if (el) el.scrollIntoView({ behavior: 'auto', block: 'start' })
  }, [location.hash])

  // Active = topmost entry currently in viewport. IntersectionObserver delivers
  // entries in DOM order; without explicit boundingClientRect.top sort, the
  // chosen "active" can drift to a non-top entry when 2-3 short entries
  // overlap the visible band simultaneously.
  useEffect(() => {
    if (items.length === 0) return
    const observer = new IntersectionObserver(
      visibleEntries => {
        const visible = visibleEntries.filter(e => e.isIntersecting)
        if (visible.length === 0) return
        const topmost = visible.reduce((a, b) =>
          a.boundingClientRect.top < b.boundingClientRect.top ? a : b,
        )
        setActiveAnchor(topmost.target.id)
      },
      { rootMargin: '-10% 0px -70% 0px', threshold: 0 },
    )
    document.querySelectorAll('[data-changelog-entry]').forEach(el => {
      observer.observe(el)
    })
    return () => observer.disconnect()
  }, [items.length])

  return (
    <div className="max-w-6xl mx-auto px-6">
      <header className="py-10 md:py-14 border-b border-gray-200 dark:border-gray-800">
        <h1 className="text-3xl md:text-4xl font-semibold text-gray-900 dark:text-gray-100">
          Журнал изменений
        </h1>
        <p className="mt-3 text-base text-gray-600 dark:text-gray-400 max-w-2xl">
          Изменения в МММ — новые фичи, улучшения и фиксы. Версия веб-клиента:{' '}
          <span className="font-mono font-semibold text-gray-900 dark:text-gray-100 tabular-nums">
            v{__APP_VERSION__}
          </span>
        </p>
      </header>

      <div className="py-10 lg:flex lg:gap-12">
        <main className="flex-1 min-w-0 space-y-12 lg:space-y-16">
          {items.length === 0 ? (
            <p className="text-sm text-gray-500 dark:text-gray-400">
              Пока ничего нового.
            </p>
          ) : (
            items.map(({ entry, anchor, dateLong }) => (
              <ChangelogEntryView
                key={anchor}
                entry={entry}
                anchor={anchor}
                dateLong={dateLong}
              />
            ))
          )}
        </main>

        <aside className="hidden lg:block w-48 shrink-0">
          <nav
            aria-label="На этой странице"
            className="sticky top-8 max-h-[calc(100vh-4rem)] overflow-y-auto"
          >
            <h2 className="text-xs font-semibold uppercase tracking-wide text-gray-500 dark:text-gray-400 mb-3">
              На этой странице
            </h2>
            <ul className="space-y-1.5">
              {items.map(({ anchor, dateShort }) => (
                <li key={anchor}>
                  <a
                    href={`#${anchor}`}
                    className={`block text-sm transition-colors tabular-nums ${
                      activeAnchor === anchor
                        ? 'text-blue-600 dark:text-blue-400 font-semibold'
                        : 'text-gray-500 dark:text-gray-400 hover:text-gray-900 dark:hover:text-gray-100'
                    }`}
                  >
                    {dateShort}
                  </a>
                </li>
              ))}
            </ul>
          </nav>
        </aside>
      </div>
    </div>
  )
}

interface EntryViewProps {
  entry: ChangelogEntry
  anchor: string
  dateLong: string
}

function ChangelogEntryView({ entry, anchor, dateLong }: EntryViewProps) {
  return (
    <section
      data-changelog-entry
      id={anchor}
      className="md:flex md:gap-8 scroll-mt-24"
    >
      <div className="md:w-40 md:shrink-0 mb-4 md:mb-0">
        <div className="text-2xl font-semibold text-gray-900 dark:text-gray-100 tabular-nums whitespace-nowrap">
          {entry.date}
        </div>
        <div className="mt-1 text-sm text-gray-500 dark:text-gray-400">
          {dateLong}
        </div>
        {entry.versions && entry.versions.length > 0 && (
          <div className="mt-2.5 flex flex-col gap-1">
            {entry.versions.map(v => {
              const accent = DOMAIN_ACCENT[v.domain] ?? DOMAIN_ACCENT_FALLBACK
              return (
                <span
                  key={v.domain}
                  className={`inline-flex items-center gap-1.5 self-start rounded-md ${accent.bg} pl-1.5 pr-2 py-0.5 text-xs`}
                >
                  <span className={`h-1.5 w-1.5 rounded-full ${accent.dot}`} />
                  <span className={`font-medium ${accent.label}`}>{v.domain}</span>
                  <span className="font-mono tabular-nums text-gray-500 dark:text-gray-400">
                    {v.version}
                  </span>
                </span>
              )
            })}
          </div>
        )}
      </div>
      <div className="flex-1 min-w-0 space-y-4">
        {entry.blocks.map((block, idx) => (
          <BlockView key={idx} block={block} />
        ))}
      </div>
    </section>
  )
}
